import React from 'react';
import {
  Loading,
  RecordContextProvider,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';

type IFormAudit = {
  title: string;
  language: string;
  welcomeTitle: string;
  welcomeMessage: string;
  thankYouTitle: string;
  thankYouMessage: string;
  skipWelcomePage: boolean;
};

const FormAuditDetails = () => {
  const record = useRecordContext<AuditTrack<IFormAudit>>();
  const { data, isLoading } = useGetOne<AuditTrack<IFormAudit>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<IFormAudit>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    title: resourceData.title?.hasChanged,
    language: resourceData.language?.hasChanged,
    welcomeTitle: resourceData.welcomeTitle?.hasChanged,
    welcomeMessage: resourceData.welcomeMessage?.hasChanged,
    thankYouTitle: resourceData.thankYouTitle?.hasChanged,
    thankYouMessage: resourceData.thankYouMessage?.hasChanged,
    skipWelcomePage: resourceData.skipWelcomePage?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.title && <TextDiffField source="title" label="Title" />}
          {hasFieldChanges.language && <TextDiffField source="language" label="Language" />}
          {hasFieldChanges.welcomeTitle && <TextDiffField source="welcomeTitle" label="Welcome title" />}
          {hasFieldChanges.welcomeMessage && <TextDiffField source="welcomeMessage" label="Welcome message" />}
          {hasFieldChanges.thankYouTitle && <TextDiffField source="thankYouTitle" label="Thank you title" />}
          {hasFieldChanges.thankYouMessage && <TextDiffField source="thankYouMessage" label="Thank you message" />}
          {hasFieldChanges.skipWelcomePage && <TextDiffField source="skipWelcomePage" label="Skip welcome page" />}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default FormAuditDetails;
